// src/shared/navigation/SideMenu/pathReachability.ts
import {
	canAccessKontrolAlerts,
	type KontrolAlertsSelectionInput,
} from "@shared/navigation/helpers/canAccessKontrolAlerts";
import {
	adminSections,
	filterAdminSectionsByPermission,
	type AdminSection,
} from "./components/AdminSections";
import {
	accountAreaItems,
	eventManagementItems,
	insightMenuItems,
	mainItems,
	managementItems,
	reflectorV2MenuItems,
	serviceItems,
	type MenuItem,
} from "./MenuData";
import { findMenuItemByPath } from "./MenuUtils";
import {
	normalizeProduct,
	resolveInternalNameFromPathname,
	type NormalizedProduct,
} from "./productConfig";
import { isProductionEnvironment } from "./sideMenuEnv";
import {
	filterAccountAreaItems,
	filterKontrolEventManagementItems,
	isAdminCenterShellPath,
	isKontrolAdminCenterPath,
} from "./sideMenuVisibility";

/**
 * Can this tenant get to this route? (CTX-516)
 *
 * The What's new CTA has to decide before it renders: a `Walk me through it`
 * that lands on a screen the tenant does not have is worse than the deep link
 * it replaces. No existing helper answers this, because the rail never had to
 * ask it — `SideMenu` builds the tree for the **one product you are looking
 * at**, while a changelog entry can point anywhere in the app.
 *
 * So this composes the rules rather than inventing any: `normalizeProduct` and
 * `resolveInternalNameFromPathname` for entitlement, and the same
 * `filterAccountAreaItems` /
 * `filterKontrolEventManagementItems` / `filterAdminSectionsByPermission` the
 * rail filters its rows with. Nothing here re-decides who may see what; if one
 * of those rules changes, this follows.
 *
 * ── Shape of the answer ─────────────────────────────────────────────────────
 *
 * Two questions, in order:
 *
 * 1. **Does the tenant own the product that route belongs to?** This is the
 *    gate that matters in practice — `MenuItem` carries no permission field,
 *    so for an ordinary product screen entitlement is the whole answer.
 * 2. **If the route belongs to a menu row, does that row survive this tenant's
 *    filters?** Matched by row id, not by a second path lookup: a gated-away
 *    row can still prefix-match an unrelated ancestor, which would read as
 *    reachable.
 *
 * A route that belongs to **no** menu row — a detail screen nested under one,
 * say — is decided by (1) alone, since `findMenuItemByPath` already resolves a
 * nested route to its parent row.
 *
 * ── Deliberately conservative ───────────────────────────────────────────────
 *
 * Unknown routes and the Admin Center's email whitelist both resolve to "not
 * reachable". The cost of a false negative is the deep link the entry would
 * have shown anyway; the cost of a false positive is a CTA that dead-ends.
 * The whitelist in particular is a developer backdoor, not an entitlement, and
 * a customer-facing button should not honour it.
 */
export interface PathReachabilityInput {
	/** `userProfile.ProductList` — the tenant's entitlements. */
	productList: { id: number; name: string }[] | undefined;
	/** `userProfile.TenantType`. */
	tenantType: string | undefined;
	/** `userProfile.ListPermiso`, for the Admin Center sections. */
	permissions: string[] | undefined;
	/**
	 * Partner-view inputs for the Alerts audience rule. Absent is read as "not
	 * in partner view", which denies the Alerts rows — the safe direction.
	 */
	alertsSelection?: KontrolAlertsSelectionInput;
}

/** Query and hash are addressing; the tenant reaches a screen, not a fragment. */
function toPathname(path: string): string {
	return path.split("?")[0].split("#")[0];
}

/**
 * Every menu row that exists in this build, before any tenant filtering.
 * Used only to find out **which** row a route belongs to — never to decide
 * that the row is visible.
 */
const ALL_MENU_TREES: MenuItem[] = [
	...mainItems,
	...serviceItems,
	...eventManagementItems,
	...managementItems,
	...accountAreaItems,
	...reflectorV2MenuItems,
	...insightMenuItems,
];

/** A row's stable handle. Mirrors `expandMatchingParents`, which also falls back to the label. */
function rowKey(item: MenuItem): string {
	return item.id ?? item.label;
}

function forestContainsRow(items: MenuItem[], key: string): boolean {
	return items.some(
		(item) => rowKey(item) === key || (item.children ? forestContainsRow(item.children, key) : false)
	);
}

/**
 * The rows this tenant keeps, with every filter the rail applies.
 *
 * Product membership is deliberately *not* applied here — question (1) already
 * settled it, and a product's rows are unreachable for want of the product,
 * not for want of a row.
 */
function survivingRows(input: PathReachabilityInput, isProduction: boolean): MenuItem[] {
	const { tenantType, alertsSelection } = input;
	const showKontrolAlerts = alertsSelection
		? canAccessKontrolAlerts(tenantType ?? "", alertsSelection)
		: false;

	return [
		...mainItems,
		...serviceItems,
		...managementItems,
		...insightMenuItems,
		...filterKontrolEventManagementItems(eventManagementItems, tenantType, showKontrolAlerts),
		...reflectorV2MenuItems,
		// The Account section: rail rows since CTX-512, and the one place where a
		// route's reachability depends on a rule rather than on entitlement.
		...filterAccountAreaItems(accountAreaItems, tenantType, isProduction),
	];
}

function adminSectionMatches(sections: AdminSection[], pathname: string): boolean {
	return sections.some(
		(section) =>
			section.route === pathname ||
			(section.children ? adminSectionMatches(section.children, pathname) : false)
	);
}

/**
 * Admin Center routes are their own vocabulary: `AdminSection` carries `route`
 * and `requiredPermission`, where `MenuItem` carries neither. This is the only
 * place in the menu where a permission *string* gates anything.
 */
function canReachAdminCenterPath(pathname: string, input: PathReachabilityInput): boolean {
	if (input.tenantType !== "GLOBAL_ADMIN") return false;
	const permitted = filterAdminSectionsByPermission(adminSections, input.permissions ?? []);
	return adminSectionMatches(permitted, pathname);
}

export const canReachPath = (
	path: string,
	input: PathReachabilityInput,
	isProduction: boolean = isProductionEnvironment()
): boolean => {
	const pathname = toPathname(path);
	if (!pathname.startsWith("/")) return false;

	if (isKontrolAdminCenterPath(pathname) || isAdminCenterShellPath(pathname)) {
		return canReachAdminCenterPath(pathname, input);
	}

	// (1) Entitlement. An empty owner means the route belongs to no product —
	// `/account/*` and friends — and is decided by its row alone.
	const owner = resolveInternalNameFromPathname(pathname);
	if (owner) {
		const owned = (input.productList ?? [])
			.map(normalizeProduct)
			.filter((product): product is NormalizedProduct => product !== null)
			.map((product) => product.internalName);
		if (!owned.includes(owner)) return false;
	}

	// (2) The row, when there is one.
	const row = findMenuItemByPath(ALL_MENU_TREES, pathname);
	if (!row) return !!owner;

	return forestContainsRow(survivingRows(input, isProduction), rowKey(row));
};
